import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import "./rules.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { notifyContentChanged } from "../../components/AdminNav";
import { resolveCrossroadsTheme } from "../../../utils/crossroadsTheme";

const TABS = [
  { id: "general", label: "General", icon: "fa-gear" },
  { id: "branding", label: "Branding & Theme", icon: "fa-palette" },
  { id: "content", label: "Game Content", icon: "fa-pen-to-square" },
  { id: "completion", label: "Completion Screen", icon: "fa-flag-checkered" },
  { id: "live", label: "Live Audience", icon: "fa-tower-broadcast" },
];

// [saved key, label, what it colours]. Keys stay the same so saved themes keep working.
const COLOR_FIELDS = [
  ["text_color", "Text colour", "General text on the page"],
  ["primary_color", "Heading colour", "Popup titles, scores and highlighted numbers"],
  ["secondary_color", "Label colour", "Score / timer pills, timer badge and outline buttons"],
  ["button_background_color", "Button colour", "Main buttons (OK, Continue, Next)"],
  ["button_text_color", "Button text colour", "Text on the main buttons"],
  ["popup_color", "Popup background colour", "Background of popups and cards"],
  ["popup_text_color", "Popup text colour", "Text inside popups and cards"],
  ["path_a_color", "Road A colour", "Left road and its option card (unless a crossroads sets its own)"],
  ["path_b_color", "Road B colour", "Right road and its option card (unless a crossroads sets its own)"],
];

const LIMITS = {
  intro_title: 80,
  intro_text: 300,
  leaderboard_message: 300,
  rule: 200,
  message: 120,
  dyk_default_heading: 60,
  completion_title: 80,
  completion_message: 400,
};
const HEX_RE = /^#[0-9a-f]{6}$/i;

/** Build the editable form from saved theme data (with Crossroads defaults filled in). */
function formFromTheme(data) {
  const t = resolveCrossroadsTheme(data);
  const asBool = (v, d) => (v === true || v === "true" ? true : v === false || v === "false" ? false : d);
  return {
    game_title: t.title,
    game_mode: t.mode,
    shuffle_questions: asBool(data?.shuffle_questions, false),
    show_completion_time: asBool(data?.show_completion_time, true),
    leaderboard_enabled: asBool(data?.leaderboard_enabled, true),
    leaderboard_message: t.leaderboardMessage,
    has_correct_answers: asBool(data?.has_correct_answers, true),
    primary_color: t.colors.primary,
    secondary_color: t.colors.secondary,
    text_color: t.colors.text,
    button_background_color: t.colors.button,
    button_text_color: t.colors.buttonText,
    popup_color: t.colors.popup,
    popup_text_color: t.colors.popupText,
    path_a_color: t.colors.pathA,
    path_b_color: t.colors.pathB,
    intro_title: t.introTitle,
    intro_text: t.introText,
    rules: t.rules,
    success_messages: t.successMessages,
    incorrect_messages: t.incorrectMessages,
    dyk_default_heading: t.dykHeading,
    completion_title: t.completionTitle,
    completion_message: t.completionMessage,
    voting_duration_seconds: String(t.votingSeconds),
  };
}

function Field({ label, hint, count, max, children, htmlFor }) {
  return (
    <div className="as-field">
      <div className="as-field-head">
        <label htmlFor={htmlFor}>{label}</label>
        {max ? (
          <span className={`as-count${count > max ? " is-over" : ""}`}>
            {count}/{max}
          </span>
        ) : null}
      </div>
      {children}
      {hint ? <p className="as-hint">{hint}</p> : null}
    </div>
  );
}

function Toggle({ id, label, hint, checked, onChange }) {
  return (
    <div className="as-toggle-row">
      <div>
        <label htmlFor={id} className="as-toggle-label">
          {label}
        </label>
        {hint ? <p className="as-hint">{hint}</p> : null}
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        className={`as-switch${checked ? " is-on" : ""}`}
        onClick={() => onChange(!checked)}
      >
        <span />
      </button>
    </div>
  );
}

function ListEditor({ id, items, onChange, max, maxItems, minItems = 0, placeholder, addLabel }) {
  const update = (i, v) => onChange(items.map((x, j) => (j === i ? v.slice(0, max) : x)));
  return (
    <div className="as-list" id={id}>
      {items.map((item, i) => (
        <div className="as-list-row" key={i}>
          <span className="as-list-num">{i + 1}.</span>
          <textarea
            rows={2}
            value={item}
            maxLength={max}
            placeholder={placeholder}
            aria-label={`${placeholder} ${i + 1}`}
            onChange={(e) => update(i, e.target.value)}
          />
          <button
            type="button"
            className="as-icon-btn"
            aria-label={`Remove item ${i + 1}`}
            onClick={() => onChange(items.filter((_, j) => j !== i))}
            disabled={items.length <= minItems}
          >
            <i className="fa-solid fa-xmark" aria-hidden />
          </button>
        </div>
      ))}
      <button type="button" className="as-link-btn" onClick={() => onChange([...items, ""])} disabled={items.length >= maxItems}>
        + {addLabel}
      </button>
      <p className="as-hint">
        Up to {maxItems}, {max} characters each.
      </p>
    </div>
  );
}

const Rules = () => {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, status, data } = useSelector((state) => state.theme);

  const [tab, setTab] = useState("general");
  const [form, setForm] = useState(() => formFromTheme(null));
  const [loadedFor, setLoadedFor] = useState(null);
  const [saving, setSaving] = useState(false);

  // Always load the latest saved values on mount so a refresh never shows stale defaults.
  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data || !data.themename) return;
    if (loadedFor === data.themename) return;
    setForm(formFromTheme(data));
    setLoadedFor(data.themename);
  }, [data, loadedFor]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const text = (key) => (e) => set(key)(e.target.value.slice(0, LIMITS[key] || 1000));

  const navigate = useNavigate();

  const validate = () => {
    if (!form.rules.map((r) => r.trim()).filter(Boolean).length) return ["content", "Add at least one instruction for How to Play."];
    for (const [key, label] of COLOR_FIELDS) {
      if (!HEX_RE.test(form[key])) return ["branding", `${label} must be a hex colour like #2E86DE.`];
    }
    if (form.path_a_color.toLowerCase() === form.path_b_color.toLowerCase()) {
      return ["branding", "Road A and Road B colours must be different so players can tell the roads apart."];
    }
    const secs = Number(form.voting_duration_seconds);
    if (!Number.isInteger(secs) || secs < 5 || secs > 600) return ["live", "Voting duration must be between 5 and 600 seconds."];
    return null;
  };

  const handleSave = async () => {
    if (!adminToken) {
      Swal.fire("Session expired", "Please open the admin panel again from your game.", "error");
      return;
    }
    const problem = validate();
    if (problem) {
      setTab(problem[0]);
      Swal.fire("Check this field", problem[1], "warning");
      return;
    }
    const clean = (arr) => arr.map((s) => s.trim()).filter(Boolean);
    const payload = {
      data: {
        ...form,
        shuffle_questions: form.shuffle_questions ? "true" : "false",
        show_completion_time: form.show_completion_time ? "true" : "false",
        leaderboard_enabled: form.leaderboard_enabled ? "true" : "false",
        leaderboard_message: form.leaderboard_message.trim(),
        has_correct_answers: form.has_correct_answers ? "true" : "false",
        // Legacy keys some older screens still read
        box_bg_color: form.button_background_color,
        box_text_color: form.button_text_color,
        rules: clean(form.rules),
        success_messages: clean(form.success_messages),
        incorrect_messages: clean(form.incorrect_messages),
        voting_duration_seconds: String(Number(form.voting_duration_seconds)),
      },
      organizationId: organizationId || "admin",
      sessionId: sessionId || "admin",
      currentTheme,
    };
    setSaving(true);
    try {
      await updateThemeData({ payload, token: adminToken });
      await dispatch(fetchThemeData({ themeId: currentTheme }));
      notifyContentChanged();
      Swal.fire({
        icon: "success",
        title: "Saved as draft",
        text: "Publish when you're ready for players to see it.",
      });
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!currentTheme) {
    return (
      <div className="as-page">
        <p className="as-empty">Select a theme on the admin home page first.</p>
      </div>
    );
  }

  return (
    <div className="as-page">
      <div className="as-head">
        <div>
          <h1>Game Settings</h1>
          <p>Branding, wording and how the game runs. Changes are saved as a draft until you Publish.</p>
        </div>
        <button type="button" className="as-btn as-btn--primary" onClick={handleSave} disabled={saving || status === "loading"} data-tour="settings-save">
          {saving ? "Saving…" : "Save Draft"}
        </button>
      </div>

      <div className="as-layout">
        <div className="as-tabs" role="tablist" aria-label="Settings sections" data-tour="settings-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`as-tab-${t.id}`}
              aria-selected={tab === t.id}
              aria-controls={`as-panel-${t.id}`}
              className={`as-tab${tab === t.id ? " is-active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              <i className={`fa-solid ${t.icon}`} aria-hidden /> {t.label}
            </button>
          ))}
        </div>

        <div className="as-panel" role="tabpanel" id={`as-panel-${tab}`} aria-labelledby={`as-tab-${tab}`}>
          {tab === "general" ? (
            <>
              <h2>General</h2>
              <fieldset className="as-field as-radio-group">
                <legend>Game mode</legend>
                {[
                  ["self_paced", "Self Paced", "Each player walks the journey on their own device, at their own pace."],
                  ["live", "Live Audience", "You run the game from Live Control; players vote from their own devices as you release each crossroads."],
                ].map(([value, label, hint]) => (
                  <label key={value} className={`as-radio${form.game_mode === value ? " is-checked" : ""}`}>
                    <input type="radio" name="game_mode" value={value} checked={form.game_mode === value} onChange={() => set("game_mode")(value)} />
                    <span>
                      <strong>{label}</strong>
                      <small>{hint}</small>
                    </span>
                  </label>
                ))}
              </fieldset>
              <Toggle
                id="has_correct_answers"
                label="Crossroads have a correct answer"
                hint={
                  form.has_correct_answers
                    ? "On: each crossroads has one right road that earns points (set it per crossroads)."
                    : "Off: choice based — no right or wrong road, no points and no accuracy. Players just pick what they would do."
                }
                checked={form.has_correct_answers}
                onChange={set("has_correct_answers")}
              />
              <Toggle id="shuffle_questions" label="Shuffle crossroads order" hint="Each player (or live run) gets the crossroads in a random order." checked={form.shuffle_questions} onChange={set("shuffle_questions")} />
              <Toggle
                id="leaderboard_enabled"
                label="Show leaderboard"
                hint={
                  form.leaderboard_enabled
                    ? "On: after the game, players see the leaderboard."
                    : "Off: players see only their own score and time, plus your message below."
                }
                checked={form.leaderboard_enabled}
                onChange={set("leaderboard_enabled")}
              />
              {!form.leaderboard_enabled ? (
                <Field
                  label="Message instead of the leaderboard"
                  htmlFor="leaderboard_message"
                  count={form.leaderboard_message.length}
                  max={LIMITS.leaderboard_message}
                  hint="Optional. Shown under the player's score, e.g. “Winners will be announced at the town hall.”"
                >
                  <textarea id="leaderboard_message" rows={3} value={form.leaderboard_message} onChange={text("leaderboard_message")} />
                </Field>
              ) : null}
              <Toggle id="show_completion_time" label="Show time taken" hint="Timer during the journey and total time on the completion screen." checked={form.show_completion_time} onChange={set("show_completion_time")} />
            </>
          ) : null}

          {tab === "branding" ? (
            <>
              <h2>Theme colours</h2>
              <div className="as-colors">
                {COLOR_FIELDS.map(([key, label, hint]) => (
                  <div className="as-color" key={key}>
                    <input type="color" aria-label={label} value={HEX_RE.test(form[key]) ? form[key] : "#000000"} onChange={(e) => set(key)(e.target.value)} />
                    <div>
                      <label htmlFor={`hex-${key}`}>{label}</label>
                      <input id={`hex-${key}`} className="as-hex" value={form[key]} maxLength={7} onChange={(e) => set(key)(e.target.value.trim())} />
                      {hint ? <small>{hint}</small> : null}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : null}

          {tab === "content" ? (
            <>
              <h2>Introduction</h2>
              <Field label="Introduction title" htmlFor="intro_title" count={form.intro_title.length} max={LIMITS.intro_title}>
                <input id="intro_title" value={form.intro_title} onChange={text("intro_title")} />
              </Field>
              <Field label="Introduction text" htmlFor="intro_text" count={form.intro_text.length} max={LIMITS.intro_text} hint="Sets the context before the journey begins.">
                <textarea id="intro_text" rows={4} value={form.intro_text} onChange={text("intro_text")} />
              </Field>
              <h2>How to Play instructions</h2>
              <ListEditor id="rules" items={form.rules} onChange={set("rules")} max={LIMITS.rule} maxItems={6} minItems={1} placeholder="Instruction" addLabel="Add instruction" />
              <h2>Feedback messages</h2>
              <p className="as-hint">One is picked for each decision on a crossroads with a correct answer.</p>
              <h3>Success messages</h3>
              <ListEditor id="success_messages" items={form.success_messages} onChange={set("success_messages")} max={LIMITS.message} maxItems={6} placeholder="Success message" addLabel="Add success message" />
              <h3>Incorrect messages</h3>
              <ListEditor id="incorrect_messages" items={form.incorrect_messages} onChange={set("incorrect_messages")} max={LIMITS.message} maxItems={6} placeholder="Incorrect message" addLabel="Add incorrect message" />
              <Field label="Default “Did You Know?” heading" htmlFor="dyk_default_heading" count={form.dyk_default_heading.length} max={LIMITS.dyk_default_heading} hint="Used when a crossroads has no heading of its own.">
                <input id="dyk_default_heading" value={form.dyk_default_heading} onChange={text("dyk_default_heading")} />
              </Field>
            </>
          ) : null}

          {tab === "completion" ? (
            <>
              <h2>Completion screen</h2>
              <Field label="Completion title" htmlFor="completion_title" count={form.completion_title.length} max={LIMITS.completion_title}>
                <input id="completion_title" value={form.completion_title} onChange={text("completion_title")} />
              </Field>
              <Field label="Completion message" htmlFor="completion_message" count={form.completion_message.length} max={LIMITS.completion_message}>
                <textarea id="completion_message" rows={4} value={form.completion_message} onChange={text("completion_message")} />
              </Field>
            </>
          ) : null}

          {tab === "live" ? (
            <>
              <h2>Live Audience</h2>
              {form.game_mode !== "live" ? (
                <p className="as-callout">
                  Game mode is <strong>Self Paced</strong>. Switch it to Live Audience on the General tab (and Publish) so players' Play button opens voting.
                </p>
              ) : null}
              <Field label="Voting time per crossroads (seconds)" htmlFor="voting_duration_seconds" hint="5–600 seconds. The host can also close voting early.">
                <input
                  id="voting_duration_seconds"
                  type="number"
                  min={5}
                  max={600}
                  value={form.voting_duration_seconds}
                  onChange={(e) => set("voting_duration_seconds")(e.target.value)}
                />
              </Field>
              <div className="as-inline">
                <button type="button" className="as-btn as-btn--primary" onClick={() => navigate("/admin/live")}>
                  <i className="fa-solid fa-tower-broadcast" aria-hidden /> Open Live Control
                </button>
              </div>
              <p className="as-hint">
                Players open the game from their normal link and wait. From Live Control you start the game and release each crossroads — players see every step instantly. It plays the <strong>published</strong> version of the game.
              </p>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Rules;
